import axios from 'axios';
import PURCHASE_ORDER_STRINGS from '#client/pages/PurchaseOrders/constants/purchaseOrders.en.json';

/**
 * Fetches product details from the server based on barcode or SKU.
 * @param {Object} params - Contains barcode, sku, poId, and rdId.
 * @param {Function} onProductFetched - Callback function to handle the fetched product.
 * @param {Function} onError - Callback function to handle errors.
 */
export const getTotalQuantityReceivedForSkuAcrossDocs = (sku, purchaseOrder) => {
    if (!purchaseOrder) return null;
    const poDocs = purchaseOrder.receivingDocuments;

    return poDocs.reduce((sum, doc) => {
        const lines = doc.lines;
        const matchingLines = lines.filter((line) => String(line?.sku).trim().toLowerCase() === String(sku).trim().toLowerCase());
        const docSum = matchingLines.reduce((lineSum, line) => lineSum + Number(line?.quantityReceived), 0);
        return sum + docSum;
    }, 0);
};

export const getQuantityExpectedForSku = (sku, purchaseOrder) => {
    if (!purchaseOrder || !sku) return null;

    const line = purchaseOrder.purchaseOrderLines.find((l) => l.sku.trim().toLowerCase() === sku.trim().toLowerCase());

    if (!line) return;
    return Number(line.quantity);
};

export const fetchProductFromServer = async ({ barcode = '', sku, poId, rdId, purchaseOrder }) => {
    // If there is no barcode or sku, throw an error and exit early.
    if (!barcode && !sku) {
        throw new Error(`${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.BARCODE_OR_SKU_REQUIRED_ERROR}`);
    }

    try {
        const response = await axios.post(
            `${import.meta.env.VITE_API_URL}/api/app/purchase-orders/${poId}/receiving-document/${rdId}`,
            { barcode, sku, action: 'fetch-product' },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        if (response.status !== 200) {
            throw new Error(response.data?.error || PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.FAILED_TO_FETCH_PRODUCT);
        }

        const data = response.data.data;
        const productData = data?.productData;
        const [variant] = productData?.data ?? [];
        // Read off the order the page already holds rather than having the scan
        // endpoint resend every line on every scan.
        const quantityExpected = getQuantityExpectedForSku(variant?.sku || sku, purchaseOrder);

        if (!variant) {
            throw new Error(
                barcode && sku
                    ? `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.NO_PRODUCT_FOUND_FOR_BARCODE}: ${barcode} ${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.AND_SKU}: ${sku}`
                    : barcode
                      ? `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.NO_PRODUCT_FOUND_FOR_BARCODE}: ${barcode}`
                      : sku
                        ? `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.NO_PRODUCT_FOUND_FOR_SKU}: ${sku}`
                        : `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.NO_PRODUCT_FOUND}`
            );
        }

        // quantityReceived is not returned: nothing read it, and it was always 0
        // because the server never selected the sku the filter matched on.
        // Callers use getTotalQuantityReceivedForSkuAcrossDocs instead.
        return {
            variant,
            barcode: variant.barcode || barcode || sku,
            quantityExpected,
        };
    } catch (err) {
        throw new Error(`${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.FETCHING_PRODUCTS_SERVER_ERROR}`);
    }
};

/**
 * Pauses execution for a specified duration.
 * @param {*} setActive
 * @param {*} setOtherActive
 * @param {*} setEntryMode
 */
export const toggleActiveWithEntryMode = async (setActive, setOtherActive, setEntryMode) => {
    setActive((prev) => {
        const next = !prev;
        if (next) {
            setOtherActive(false);
            setEntryMode('scan-mode');
        } else {
            setEntryMode('initial-load-mode');
        }
        return next;
    });
};

/**
 * Handles toggling receiving line items and setting entry mode.
 * @param {Function} setReceivingLineItemsActive - Function to set receiving line items active state.
 * @param {Function} setReceivingCartonsActive - Function to set receiving cartons active state.
 * @param {Function} setEntryMode - Function to set the entry mode.
 * @returns {Promise<void>} - Toggles the active state and sets the entry mode.
 */
export const handleReceivingLineItems = async (setReceivingLineItemsActive, setReceivingCartonsActive, setEntryMode) => {
    return toggleActiveWithEntryMode(setReceivingLineItemsActive, setReceivingCartonsActive, setEntryMode);
};

/**
 * Handles toggling receiving cartons and setting entry mode.
 * @param {Function} setReceivingCartonsActive - Function to set receiving cartons active state.
 * @param {Function} setReceivingLineItemsActive - Function to set receiving line items active state.
 * @param {Function} setEntryMode - Function to set the entry mode.
 * @returns {Promise<void>} - Toggles the active state and sets the entry mode.
 */
export const handleReceivingCarton = async (setReceivingCartonsActive, setReceivingLineItemsActive, setEntryMode) => {
    return toggleActiveWithEntryMode(setReceivingCartonsActive, setReceivingLineItemsActive, setEntryMode);
};

export const fetchCartonLinesByLicensePlate = async (poId, rdId, cartonLicensePlateNumber) => {
    try {
        const response = await axios.post(
            `${import.meta.env.VITE_API_URL}/api/app/purchase-orders/${poId}/receiving-document/${rdId}`,
            {
                action: 'fetch-carton',
                licensePlate: cartonLicensePlateNumber,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        if (!response) return {};

        return response.data.data;
    } catch (err) {
        throw new Error(`${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.CARTON_NOT_FOUND_ERROR}`);
    }
};

export const getScanModeStatus = (receivingMode, receivingCartonsActive, receivingLineItemsActive) => {
    if (receivingMode === 'receiving-cartons' && receivingCartonsActive) {
        return `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.CURRENTLY_RECEIVING_CARTONS}`;
    }
    if (receivingMode === 'receiving-line-items' && receivingLineItemsActive) {
        return `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.CURRENTLY_RECEIVING_LINE_ITEMS}`;
    }
    if (!receivingLineItemsActive && !receivingCartonsActive) {
        return `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.SELECT_RECEIVING_MODE}`;
    }
    return null;
};

export const getResourceId = (entry) => {
    return [entry.variant?.id, entry.barcode].filter(Boolean).join('-');
};
